import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useOutletContext } from 'react-router-dom';
import EditProfileModal from '../../components/Common/EditProfileModal';
import ChangePasswordModal from '../../components/Common/ChangePasswordModal';
import { CoverPattern, shade } from '../../components/Admin/CourseCover';
import { CourseIcon } from '../../components/Admin/iconLibrary';
import { avatarColor, AvatarIcon } from '../../utils/avatar';
import { getProgressCode, getProgress, getVariants, getHomeworks } from '../../services/studentService';
import { getNotifyPrefs, setNotifyPrefs } from '../../services/notificationsService';
import useTelegramLink from '../../hooks/useTelegramLink';
import { computeCourseProgress } from '../../utils/progressStats';
import { updateProfile } from '../../services/authService';

const IcFire = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>);
const IcCheck = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>);
const IcClip = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="2"/></svg>);
// «Осталось» — песочные часы, те же, что в карточке ученика у админа
const IcHourglass = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2h12M6 22h12M8 2v4a4 4 0 0 0 4 4 4 4 0 0 0 4-4V2M8 22v-4a4 4 0 0 1 4-4 4 4 0 0 1 4 4v4"/></svg>);
const IcInfo = () => (<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>);
const IcTelegram = () => (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M22 3 2 10.5l6 2.5 2.5 6L14 15l6 6z"/><path d="m8 13 6-4"/></svg>);
// Самолётик Telegram — тот же, что в карточке «Куратор в Telegram»
const IcTgPlane = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M22 3 2 10.5l6 2.5 2.5 6L14 15l6 6z"/><path d="m8 13 6-4"/></svg>);
const IcChevronDown = () => (<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>);
const IcTick = () => (<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>);
const IcCopy = () => (<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>);


// Простой iOS-тумблер
const Switch = ({ on, onChange, disabled }) => (
  <button type="button" onClick={() => !disabled && onChange(!on)} disabled={disabled}
    style={{ width: 46, height: 27, borderRadius: 20, border: 'none', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.45 : 1, background: on ? 'var(--primary)' : 'var(--border)', position: 'relative', transition: 'background .2s', flexShrink: 0 }}>
    <span style={{ position: 'absolute', top: 3, left: on ? 22 : 3, width: 21, height: 21, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
  </button>
);

// Русское склонение: 1 урок / 2 урока / 5 уроков
const plural = (n, f) => { const a = Math.abs(n) % 100, b = a % 10; if (a > 10 && a < 20) return f[2]; if (b > 1 && b < 5) return f[1]; if (b === 1) return f[0]; return f[2]; };

const CURATOR_TG = 'informatic_artem';

const NOTIFS = [
  { k: 'webinars', t: 'Напоминать о вебинарах', s: 'за час до начала и когда эфир пошёл' },
  { k: 'homework', t: 'Проверка ДЗ', s: 'когда куратор проверит задание' },
  { k: 'digest', t: 'Дайджест успеваемости', s: 'каждое воскресенье в 10:00 по МСК, с файлом' },
];

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const { courses = [], selectedCourseId, setSelectedCourseId, course, courseName } = useOutletContext() || {};
  const [editOpen, setEditOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [courseMenu, setCourseMenu] = useState(false);
  const [notif, setNotif] = useState(null);           // null — настройки ещё не пришли
  const [notifSoon, setNotifSoon] = useState([]);     // тумблеры, под которые логики ещё нет
  const [notifSaving, setNotifSaving] = useState(null);
  // Цель хранится на сервере; localStorage — только запасной вариант для старых сессий
  const [goal, setGoal] = useState(() => user?.goalScore || localStorage.getItem('s_goal_score') || '');

  // Раньше цель жила только в localStorage — админ её не видел. Один раз
  // поднимаем старое значение на сервер, чтобы оно появилось в карточке ученика.
  useEffect(() => {
    if (!user || user.goalScore) return;
    const legacy = localStorage.getItem('s_goal_score');
    if (!legacy) return;
    updateProfile({ goalScore: legacy })
      .then(res => updateUser({ goalScore: res.user?.goalScore || legacy }))
      .catch(() => {});
  }, [user?.id, user?.goalScore]); // eslint-disable-line
  const [progCode, setProgCode] = useState('');
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => { getProgressCode().then(r => setProgCode(r?.data?.code || '')).catch(() => {}); }, []);

  /* Достижения. «Пройдено» — сумма тех же цифр, что «Вебинары · ДЗ · Варианты»
     на Главной и в «Прогрессе» (общий расчёт computeCourseProgress): вебинар
     засчитан, как только просмотрен, ДЗ и вариант — когда сданы.
     «Лучший пробник» — максимум по завершённым вариантам: у ЕГЭ вторичный балл,
     у ОГЭ оценка. */
  const [ach, setAch] = useState({ done: null, left: null, best: null, bestKind: null });
  useEffect(() => {
    let alive = true;
    Promise.all([getProgress(selectedCourseId), getVariants(selectedCourseId), getHomeworks(selectedCourseId)])
      .then(([progRes, varRes, hwRes]) => {
        if (!alive) return;
        const vars = varRes.data || [];
        const s = computeCourseProgress(progRes.data, hwRes.data || [], vars);

        const done = vars.filter(v => v.completed);
        const ege = done.map(v => v.secondary).filter(x => typeof x === 'number');
        const oge = done.map(v => v.grade).filter(x => typeof x === 'number');
        const best = ege.length ? Math.max(...ege) : (oge.length ? Math.max(...oge) : null);
        const bestKind = ege.length ? 'ege' : (oge.length ? 'oge' : null);

        const done_ = s.web.done + s.hw.done + s.variant.done;
        const total = s.web.total + s.hw.total + s.variant.total;
        setAch({ done: done_, left: Math.max(0, total - done_), best, bestKind });
      })
      .catch(() => { if (alive) setAch({ done: null, left: null, best: null, bestKind: null }); });
    return () => { alive = false; };
  }, [selectedCourseId]);

  const achDone = ach.done == null ? '—' : `${ach.done} ${plural(ach.done, ['урок', 'урока', 'уроков'])}`;
  const achLeft = ach.left == null ? '—' : `${ach.left} ${plural(ach.left, ['урок', 'урока', 'уроков'])}`;
  const achBest = ach.best == null ? '—'
    : ach.bestKind === 'oge' ? `Оценка ${ach.best}`
      : `${ach.best} ${plural(ach.best, ['балл', 'балла', 'баллов'])}`;
  const copyCode = () => {
    if (!progCode) return;
    navigator.clipboard?.writeText(progCode).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600); }).catch(() => {});
  };

  useEffect(() => {
    if (!courseMenu) return;
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setCourseMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [courseMenu]);

  // Настройки хранятся на сервере: одинаковы на всех устройствах и реально
  // включают рассылку, а не просто галочку в браузере.
  const [tgInit, setTgInit] = useState(null);
  useEffect(() => {
    getNotifyPrefs()
      .then(r => {
        setNotif(r.data || {});
        setNotifSoon(r.soon || []);
        setTgInit(r.telegram || { available: false, connected: false, username: null });
      })
      .catch(() => {});
  }, []);
  const { tg, ready: tgReady, busy: tgBusy, waiting: tgWaiting, toggle: toggleTg } = useTelegramLink(tgInit);

  const setNotifKey = async (k, v) => {
    const prev = notif;
    setNotif({ ...(notif || {}), [k]: v });
    setNotifSaving(k);
    try {
      const r = await setNotifyPrefs({ [k]: v });
      setNotif(r.data || {});
    } catch {
      setNotif(prev);
      toast.error('Не удалось сохранить');
    } finally { setNotifSaving(null); }
  };

  /* «Доступ до» — дата с сервера: одна на все курсы, её же видит и продлевает админ
     в «Доступах». Если в сохранённом профиле её ещё нет (вошли до обновления) —
     прежний расчёт: месяц с даты зачисления. */
  const accessDate = (() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(user?.accessUntil || '');
    let d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
    if (!d) {
      const from = course?.joinedAt ? new Date(course.joinedAt) : null;
      if (from && !isNaN(from)) { d = new Date(from); d.setMonth(d.getMonth() + 1); }
    }
    if (!d || isNaN(d)) return '—';
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  })();

  const rows = [
    ['Имя', user?.name || '—'],
    ['Email', user?.email || '—'],
    ['Курс', courseName || 'Не назначен'],
    ['Куратор', 'Куратор по информатике'],
    ['Цель по баллам', goal || '—'],
  ];

  return (
    <div className="s-page prof-page">
      <div className="s-page-header">
        <div>
          <h1 className="s-page-title">Профиль</h1>
          <p className="s-page-sub">Личные данные и достижения</p>
        </div>
      </div>

      <div className="prof-wrap">
        {/* ── Левая колонка ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
          {/* Профиль */}
          <div className="panel">
            <div className="prof-hero">
              <div className="prof-ava" style={user?.avatar ? undefined : { background: avatarColor(user?.id) }}>
                {user?.avatar ? <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} /> : <AvatarIcon id={user?.id} size={34} />}
              </div>
              <div style={{ minWidth: 0 }}>
                <div className="prof-name">{user?.name || 'Ученик'}</div>
                <div className="prof-chips" ref={menuRef}>
                  <button className={`prof-course-chip${courseMenu ? ' open' : ''}`} onClick={() => courses.length > 1 && setCourseMenu(o => !o)} style={{ cursor: courses.length > 1 ? 'pointer' : 'default' }}>
                    {course && <CourseIcon icon={course.icon} size={15} sw={2} />}
                    <span className="pcc-name">{courseName || 'Курс не назначен'}</span>
                    {courses.length > 1 && <IcChevronDown />}
                  </button>
                  <span className="prof-access-chip">{user?.isTrial ? 'Пробный доступ' : `Доступ до ${accessDate}`}</span>

                  {courseMenu && (
                    <div className="prof-course-menu">
                      <div className="pcm-title">Мои курсы</div>
                      {/* Мини-обложки, как в выборе курса у админа, только ниже и шире:
                          название курса помещается в одну строку */}
                      <div className="pcm-list">
                        {courses.map(c => {
                          const col = c.color || '#7c4dff';
                          const on = c.id === selectedCourseId;
                          return (
                            <button key={c.id} type="button" className={`pcm-card${on ? ' on' : ''}`}
                              style={{ '--st-t-light': `${col}80`, '--st-t-dark': `${col}38`, '--st-chip-fg': shade(col, -42), '--st-chip-fg-dark': shade(col, 55), '--st-c': col }}
                              onClick={() => { setSelectedCourseId && setSelectedCourseId(c.id); setCourseMenu(false); }}>
                              <span className="st-crs-pat"><CoverPattern id={`pcm${c.id}`} /></span>
                              <span className="pcm-card-top">
                                {c.examType ? <span className="st-crs-lvl"><i />{c.examType}</span> : <span />}
                                <span className="pcm-card-r">
                                  {on && <span className="pcm-card-on"><IcTick /></span>}
                                  <span className="st-crs-ic"><CourseIcon icon={c.icon} size={14} sw={2} /></span>
                                </span>
                              </span>
                              <span className="pcm-card-name">{c.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="prof-rows">
              {rows.map(([k, v]) => (
                <div key={k} className="prof-row"><span className="prof-k">{k}</span><span className="prof-v">{v}</span></div>
              ))}
            </div>

            <div className="prof-actions">
              <button className="pg-btn" onClick={() => setEditOpen(true)}>Редактировать профиль</button>
              <button className="pg-btn ghost" onClick={() => setPwOpen(true)}>Сменить пароль</button>
            </div>
          </div>

          {/* Уведомления */}
          <div className="panel">
            <div className="panel-cap">Уведомления</div>
            <div className="notif-list">
              {NOTIFS.map((n, i) => {
                const soon = notifSoon.includes(n.k);
                return (
                  <React.Fragment key={n.k}>
                    <div className={`notif-row${soon ? ' soon' : ''}`}>
                      <div style={{ minWidth: 0 }}>
                        <div className="notif-t">{n.t}{soon && <span className="notif-soon">скоро</span>}</div>
                        <div className="notif-s">{n.s}</div>
                      </div>
                      {notif
                        ? <Switch on={!!notif[n.k]} disabled={soon || notifSaving === n.k} onChange={(v) => setNotifKey(n.k, v)} />
                        : <span className="ap-sw-ghost" />}
                    </div>

                    {/* Telegram — вторым в списке. Это не событие, а канал: всё,
                        что приходит в колокольчик, бот дублирует в личные сообщения. */}
                    {i === 0 && (
                      <div className={`notif-row tg-row${tg.connected ? '' : ' off'}`}>
                        <div className="tg-left">
                          <span className="tg-ic"><IcTgPlane /></span>
                          <div style={{ minWidth: 0 }}>
                            <div className="notif-t">Telegram</div>
                            <div className="notif-s">
                              {tg.connected
                                ? <>подключён{tg.username ? <> · <span className="tg-user">@{tg.username}</span></> : ''} — уведомления приходят в бота</>
                                : tgWaiting
                                  ? <span className="tg-wait"><i />ждём «Начать» в боте…</span>
                                  : tg.available ? 'присылать уведомления в личные сообщения' : 'бот скоро появится'}
                            </div>
                          </div>
                        </div>
                        {tgReady
                          ? <Switch on={tg.connected} disabled={!tg.available || tgBusy} onChange={toggleTg} />
                          : <span className="ap-sw-ghost" />}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Правая колонка ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
          {/* Куратор в Telegram */}
          <div className="panel">
            <div className="panel-cap">Куратор в Telegram</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
              <div style={{ width: 52, height: 52, borderRadius: 16, flexShrink: 0, background: 'linear-gradient(150deg,#37aee2,#1e96c8)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <IcTelegram />
                <span style={{ position: 'absolute', right: -3, bottom: -3, width: 15, height: 15, borderRadius: '50%', background: '#22c55e', border: '3px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-1)' }}>Куратор по информатике</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2 }}>Онлайн</div>
              </div>
            </div>
            <a href={`https://t.me/${CURATOR_TG}`} target="_blank" rel="noopener noreferrer" className="pg-btn" style={{ width: '100%', justifyContent: 'center', textDecoration: 'none' }}>Написать</a>
          </div>

          {/* Достижения (демо) — как было */}
          <div className="panel">
            <div className="panel-cap">Достижения</div>
            <div className="ach">
              <div className="ach-item"><div className="ach-ic fire"><IcFire /></div><div><b>12 дней<span className="demo-tag"><IcInfo />демо</span></b><span>без пропусков</span></div></div>
              <div className="ach-item"><div className="ach-ic"><IcCheck /></div><div><b>{achDone}</b><span>пройдено</span></div></div>
              <div className="ach-item"><div className="ach-ic"><IcClip /></div><div><b>{achBest}</b><span>лучший пробник</span></div></div>
              <div className="ach-item"><div className="ach-ic"><IcHourglass /></div><div><b>{achLeft}</b><span>осталось</span></div></div>
            </div>
          </div>

          {/* Индивидуальный код для родителя */}
          <div className="panel pcode-panel">
            <div className="panel-cap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span>Код для родителя</span>
              <span className="pcode-badge"><span className="pcode-dot" />Подключён</span>
            </div>
            <p className="pcode-desc">По этому коду родитель может посмотреть твою успеваемость на сайте — без входа в аккаунт.</p>
            <div className="pcode-box">
              <div className="pcode-chip"><span>{progCode || '····'}</span></div>
              <button className={`pcode-copy${copied ? ' ok' : ''}`} onClick={copyCode} disabled={!progCode} title={copied ? 'Скопировано' : 'Скопировать код'} aria-label="Скопировать код">
                {copied ? <IcTick /> : <IcCopy />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <EditProfileModal isOpen={editOpen} onClose={() => setEditOpen(false)} fallbackName="Ученик" showGoal examType={course?.examType} goal={goal} onGoalChange={(g) => { setGoal(g); localStorage.setItem('s_goal_score', g); }} />
      <ChangePasswordModal isOpen={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
};

export default ProfilePage;
